/**
 * DemoCloudEmergencyTable — Cloud 5.0 emergency alerts table with Clear action column.
 *
 * Structurally identical to the CloudAlertsPage CloudEmergencyTable but adds an Action
 * column with a Clear button per row. This is demo-only functionality —
 * the shared CloudEmergencyTable intentionally does NOT support clearing.
 *
 * Uses snake_case schema (event_id, event_datetime_utc, event_category, etc.)
 * and the same na-* and dp-* CSS classes.
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow, durationBetween } from '../../../libs.jsx';
import AlertStatusBadge from '../../events/alerts/components/AlertStatusBadge.jsx';
import AlertTypeBadge from '../../events/alerts/components/AlertTypeBadge.jsx';

function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const OVERDUE_MS = 5 * 60 * 1000;
const CRITICAL_MS = 10 * 60 * 1000;

const getDurationColor = (status, eventStart) => {
    if (status !== 'Overdue' || !eventStart) return undefined;
    const elapsed = Date.now() - new Date(eventStart.endsWith?.('Z') ? eventStart : eventStart + 'Z').getTime();
    if (elapsed > CRITICAL_MS) return 'var(--color-danger, #e63757)';
    if (elapsed > OVERDUE_MS) return 'var(--color-warning-text, #d97706)';
    return undefined;
};

const DurationCell = ({ eventStart, ackTime, status }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const ref = useRef(null);
    const hasAck = Boolean(ackTime);

    const handleEnter = () => {
        if (!hasAck || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const popoverWidth = 260;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceRight = window.innerWidth - rect.left;
        const horizontal = spaceRight < popoverWidth + 16
            ? { right: `${window.innerWidth - rect.right}px`, left: 'auto' }
            : { left: `${rect.left}px`, right: 'auto' };
        const vertical = spaceBelow < 80
            ? { bottom: `${window.innerHeight - rect.top + 6}px`, top: 'auto' }
            : { top: `${rect.bottom + 6}px`, bottom: 'auto' };
        setStyle({ position: 'fixed', ...horizontal, ...vertical });
        setOpen(true);
    };

    const color = hasAck
        ? 'var(--color-success, #0cce6b)'
        : status === 'Accepted'
            ? 'var(--color-primary, #0081C7)'
            : getDurationColor(status, eventStart);

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)}>
            <span className={`na-duration${hasAck ? ' na-duration--has-popover' : ''}`} style={color ? { color } : undefined}>
                {durationToNow(eventStart)}
            </span>
            {open && hasAck ? (
                <div className="na-status-popover" style={style}>
                    <strong>Response Time:</strong> {durationBetween(eventStart, ackTime)}
                </div>
            ) : null}
        </span>
    );
};

const DemoCloudEmergencyTable = ({ data, sortColumn, sortOrder, onSort, onClear, clearing }) => {
    useTick(1000);

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    const formatUtcToLocal = (utcStr) => {
        if (!utcStr) return '\u2014';
        try {
            const d = new Date(utcStr.endsWith('Z') ? utcStr : utcStr + 'Z');
            return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
        } catch { return utcStr; }
    };

    const getDeviceLink = (row) => {
        const mfg = parseInt(row.manufacture_id, 10);
        if (mfg === 1) return `/profiles/inovonics/${row.device_id}`;
        if (mfg === 4) return `/profiles/palcare/${row.device_id}`;
        return null;
    };

    const getBorderColor = (category) => {
        const cat = (category || '').toLowerCase();
        if (cat === 'emergency') return '#e63757';
        if (cat === 'maintenance') return '#ffc107';
        return '#0081C7';
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <th className="dp-th" style={{ textAlign: 'center' }}>Action</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('event_datetime_utc')}>Event Start{sortIcon('event_datetime_utc')}</th>
                        <th className="dp-th">Duration</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('event_category')}>Category / Type{sortIcon('event_category')}</th>
                        <th className="dp-th">Device / Serial</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('device_description')}>Description{sortIcon('device_description')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('area_name')}>Area{sortIcon('area_name')}</th>
                        <th className="dp-th">Resident</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('accepted_display')}>Accepted By / Time{sortIcon('accepted_display')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('ack_datetime_utc')}>Device Cleared{sortIcon('ack_datetime_utc')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('status')}>Status{sortIcon('status')}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={11}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">{'\u2713'}</div>
                                    <div className="na-empty__text">No active emergency alerts</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row) => {
                        const deviceLink = getDeviceLink(row);
                        return (
                            <tr key={row.event_id} className="dp-tr na-row-border" style={{ borderLeftColor: getBorderColor(row.event_category) }}>
                                <td className="dp-td" style={{ textAlign: 'center' }}>
                                    <button
                                        className="na-clear-btn"
                                        onClick={() => onClear(row.event_id)}
                                        disabled={clearing === row.event_id}
                                    >
                                        {clearing === row.event_id ? 'Clearing...' : 'Clear'}
                                    </button>
                                </td>
                                <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcToLocal(row.event_datetime_utc)}</td>
                                <td className="dp-td">
                                    <DurationCell eventStart={row.event_datetime_utc} ackTime={row.ack_datetime_utc} status={row.status} />
                                </td>
                                <td className="dp-td">
                                    <AlertTypeBadge alertType={row.event_category} alertSubType={row.event_type} />
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {deviceLink ? (
                                            <Link to={deviceLink} className="ar-link">{row.device_name || '\u2014'}</Link>
                                        ) : (<span>{row.device_name || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.manufacture_uuid || ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td">{row.device_description || '\u2014'}</td>
                                <td className="dp-td">
                                    {row.area_id ? (
                                        <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{row.area_name || '\u2014'}</Link>
                                    ) : (row.area_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    {row.resident_id ? (
                                        <Link to={`/profiles/resident/${row.resident_id}`} className="ar-link">{row.resident_name || '\u2014'}</Link>
                                    ) : (row.resident_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {row.accepted_user_id ? (
                                            <Link to={`/profiles/user/${row.accepted_user_id}`} className="ar-link">{row.accepted_display || '\u2014'}</Link>
                                        ) : (<span>{row.accepted_display || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.accepted_datetime_utc ? formatUtcToLocal(row.accepted_datetime_utc) : ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcToLocal(row.ack_datetime_utc)}</td>
                                <td className="dp-td"><AlertStatusBadge status={row.status} eventDateTime={row.event_datetime_utc} /></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default DemoCloudEmergencyTable;
